// src/features/tuition/components/dashboard/DashboardOverview.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  CircularProgress,
  Card,
  CardContent,
  Button,
  Divider,
  Avatar,
  List,
  Chip, 
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material';
import {
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    AttachMoney as MoneyIcon,
    Assignment as AssignmentIcon,
    ArrowForward as ArrowForwardIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    MoreVert as MoreVertIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon 
  } from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { 
  fetchUpcomingSessions,
  fetchRecentPayments,
  fetchStudentCount,
  fetchFinancialSummary,
  fetchTodaySessions
} from '../../services/dashboardService';

const DashboardOverview = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    studentCount: 0,
    activeStudents: 0,
    totalEarnings: 0,
    pendingPayments: 0,
    upcomingSessions: [],
    todaySessions: [],
    recentPayments: []
  });
  
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // In a real app, these would be API calls to fetch the data
        const [
          upcomingSessions,
          recentPayments,
          studentStats,
          financialSummary,
          todaySessions
        ] = await Promise.all([
          fetchUpcomingSessions(userId),
          fetchRecentPayments(userId),
          fetchStudentCount(userId),
          fetchFinancialSummary(userId),
          fetchTodaySessions(userId)
        ]);
        
        setDashboardData({
          studentCount: studentStats.total,
          activeStudents: studentStats.active,
          totalEarnings: financialSummary.total,
          pendingPayments: financialSummary.pending,
          upcomingSessions,
          todaySessions,
          recentPayments
        });
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadDashboardData();
    
    // For demo purposes, let's populate with mock data
    // This would be removed in a production app
    setTimeout(() => {
      setDashboardData({
        studentCount: 15,
        activeStudents: 12,
        totalEarnings: 8500,
        pendingPayments: 1200,
        upcomingSessions: [
          { id: 1, studentName: 'Emma Johnson', subject: 'Mathematics', date: '2025-04-19', time: '15:00', duration: 60 },
          { id: 2, studentName: 'Liam Smith', subject: 'Physics', date: '2025-04-20', time: '16:30', duration: 90 },
          { id: 3, studentName: 'Sophia Williams', subject: 'Chemistry', date: '2025-04-21', time: '14:00', duration: 60 }
        ],
        todaySessions: [
          { id: 4, studentName: 'Oliver Brown', subject: 'English', time: '17:00', duration: 60, completed: true },
          { id: 5, studentName: 'Noah Davis', subject: 'Biology', time: '18:30', duration: 60, completed: false }
        ],
        recentPayments: [
          { id: 1, studentName: 'Emma Johnson', amount: 350, date: '2025-04-15', status: 'paid' },
          { id: 2, studentName: 'Liam Smith', amount: 450, date: '2025-04-14', status: 'paid' },
          { id: 3, studentName: 'Sophia Williams', amount: 200, date: '2025-04-10', status: 'overdue' }
        ]
      });
      setLoading(false);
    }, 1000);
  }, [userId]);
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  return (
    <Box>
      {/* Stats Overview */}
      <Grid container spacing={2} sx={{ mb: 4 }} width='100%' alignItems="stretch">
        <Grid item sx={{ width: { xs: '100%', sm: '48%', md: '23%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Students
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(39, 35, 97, 0.1)', width: 40, height: 40 }}>
                  <PersonIcon sx={{ color: THEME_COLORS.primary }} />
                </Avatar>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {dashboardData.studentCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dashboardData.activeStudents} active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item sx={{ width: { xs: '100%', sm: '48%', md: '23%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Upcoming Sessions
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(233, 30, 99, 0.1)', width: 40, height: 40 }}>
                  <ScheduleIcon sx={{ color: '#E91E63' }} />
                </Avatar>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {dashboardData.upcomingSessions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This week
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item sx={{ width: { xs: '100%', sm: '48%', md: '23%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Income
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', width: 40, height: 40 }}>
                  <MoneyIcon sx={{ color: '#4CAF50' }} />
                </Avatar>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(dashboardData.totalEarnings)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item sx={{ width: { xs: '100%', sm: '48%', md: '23%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Pending Payments
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', width: 40, height: 40 }}>
                  <AssignmentIcon sx={{ color: '#FF9800' }} />
                </Avatar>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(dashboardData.pendingPayments)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Outstanding
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Main Content */}
      <Grid container spacing={2}>
        {/* Today's Sessions */}
        <Grid item sx={{ width: { xs: '100%', md: '48%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Today's Sessions
                </Typography>
                <Button 
                  variant="text" 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  sx={{ color: THEME_COLORS.primary }}
                >
                  View All
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {dashboardData.todaySessions.length > 0 ? (
                <List sx={{ width: '100%' }}>
                  {dashboardData.todaySessions.map((session) => (
                    <ListItem key={session.id} alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: THEME_COLORS.primary }}>
                          {session.studentName.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={session.studentName}
                        secondary={
                          <React.Fragment>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                            >
                              {session.subject}
                            </Typography>
                            {` — ${session.time} (${session.duration} min)`}
                          </React.Fragment>
                        }
                      />
                      <ListItemSecondaryAction>
                        {session.completed ? (
                          <Avatar sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', width: 32, height: 32 }}>
                            <CheckIcon fontSize="small" sx={{ color: '#4CAF50' }} />
                          </Avatar>
                        ) : (
                          <IconButton edge="end" sx={{ color: THEME_COLORS.primary }}>
                            <MoreVertIcon />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    No sessions scheduled for today
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Upcoming Sessions */}
        <Grid item sx={{ width: { xs: '100%', md: '48%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Upcoming Sessions
                </Typography>
                <Button 
                  variant="text" 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  sx={{ color: THEME_COLORS.primary }}
                >
                  View Calendar
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {dashboardData.upcomingSessions.length > 0 ? (
                <List sx={{ width: '100%' }}>
                  {dashboardData.upcomingSessions.map((session) => (
                    <ListItem key={session.id} alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'rgba(39, 35, 97, 0.8)' }}>
                          {session.studentName.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={session.studentName}
                        secondary={
                          <React.Fragment>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                            >
                              {session.subject}
                            </Typography>
                            {` — ${new Date(session.date).toLocaleDateString()} at ${session.time}`}
                          </React.Fragment>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" sx={{ color: THEME_COLORS.primary }}>
                          <MoreVertIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    No upcoming sessions
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Recent Payments */}
        <Grid item sx={{ width: '98%' }}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Recent Payments
                </Typography>
                <Button 
                  variant="text" 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  sx={{ color: THEME_COLORS.primary }}
                >
                  View All
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {dashboardData.recentPayments.length > 0 ? (
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px 16px', color: '#666', fontWeight: 'normal' }}>Student</th>
                        <th style={{ textAlign: 'left', padding: '8px 16px', color: '#666', fontWeight: 'normal' }}>Date</th>
                        <th style={{ textAlign: 'right', padding: '8px 16px', color: '#666', fontWeight: 'normal' }}>Amount</th>
                        <th style={{ textAlign: 'right', padding: '8px 16px', color: '#666', fontWeight: 'normal' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.recentPayments.map((payment) => (
                        <tr key={payment.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '16px', fontWeight: 'medium' }}>{payment.studentName}</td>
                          <td style={{ padding: '16px' }}>{new Date(payment.date).toLocaleDateString()}</td>
                          <td style={{ padding: '16px', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(payment.amount)}</td>
                          <td style={{ padding: '16px', textAlign: 'right' }}>
                            <Chip 
                              label={payment.status === 'paid' ? 'Paid' : 'Overdue'} 
                              size="small"
                              sx={{ 
                                bgcolor: payment.status === 'paid' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                color: payment.status === 'paid' ? '#4CAF50' : '#F44336',
                                fontWeight: 'medium'
                              }} 
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    No recent payment records
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardOverview;